import React, { useState, useRef } from 'react';
import { Box, TextField, IconButton, Popover, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { MdSend, MdKeyboardVoice, MdAttachFile, MdImage, MdVideocam, MdAudiotrack } from 'react-icons/md';

const ChatInput = ({ onSend, userLoggedIn, contactActive, typeMessagePermitted, executiveMode }) => {
    const [message, setMessage] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const attachmentInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const audioInputRef = useRef(null);
    // Añade estos estados y referencias al inicio de tu componente
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const audioChunks = useRef([]);

    // Función para iniciar la grabación
    const startRecording = async () => {
        audioChunks.current = [];

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    audioChunks.current.push(e.data);
                }
            };

            recorder.start();
            setMediaRecorder(recorder);
            return true;
        } catch (error) {
            // console.error("Error accessing microphone:", error);
            throw error;
        }
    };

    // Función para detener la grabación y obtener el blob
    const stopRecording = () => {
        return new Promise((resolve, reject) => {
            if (!mediaRecorder) {
                reject(new Error("No hay grabación en curso"));
                return;
            }

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunks.current, { type: 'audio/ogg' });

                // Liberar recursos
                mediaRecorder.stream.getTracks().forEach(track => track.stop());
                setMediaRecorder(null);

                resolve(audioBlob);
            };

            mediaRecorder.stop();
        });
    };
    // Función mejorada para enviar mensajes de texto
    const handleSend = () => {
        if (message.trim()) {
            // Enviar mensaje de texto normal
            onSend({
                type: 'text',
                content: message
            });
            setMessage('');
        }
    };

    const handleVoiceRecording = async () => {
        setIsRecording(!isRecording);

        if (isRecording) {
            try {
                // Detener la grabación y obtener el blob de audio
                const audioBlob = await stopRecording();
                // console.log("Finalizar grabación de voz");

                // Crear un objeto File a partir del Blob
                const file = new File([audioBlob], `voice_message_${Date.now()}.ogg`, {
                    type: 'audio/ogg',
                });

                // Crear FormData (igual que en handleFileChange)
                const formData = new FormData();
                formData.append('file', file);

                // Información del archivo
                const fileInfo = {
                    name: file.name,
                    size: file.size,
                    type: file.type
                };

                // Enviar igual que los otros archivos multimedia
                onSend({
                    type: 'voice',
                    file: formData,
                    fileInfo: fileInfo,
                    content: message.trim() !== '' ? message : undefined
                });

                // Limpiar mensaje si se usó como caption
                setMessage('');

            } catch (error) {
                // console.error("Error al procesar grabación de voz:", error);
            }
        } else {
            // Iniciar grabación
            try {
                await startRecording();
                // console.log("Iniciar grabación de voz");
            } catch (error) {
                // console.error("Error al iniciar grabación:", error);
                setIsRecording(false);
            }
        }
    };

    const handleAttachmentClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleAttachmentClose = () => {
        setAnchorEl(null);
    };

    const handleAttachmentSelect = (type) => {
        handleAttachmentClose();
        switch (type) {
            case 'document':
                attachmentInputRef.current.click();
                break;
            case 'image':
                imageInputRef.current.click();
                break;
            case 'video':
                videoInputRef.current.click();
                break;
            case 'audio':
                audioInputRef.current.click();
                break;
            default:
                break;
        }
    };

    // Función mejorada para enviar archivos
    const handleFileChange = (e, type) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // Crear objeto de FormData para enviar el archivo
            const formData = new FormData();
            formData.append('file', file);

            // Obtener información del archivo para mostrar en la UI
            const fileInfo = {
                name: file.name,
                size: file.size,
                type: file.type
            };

            // Enviar con el tipo de archivo correspondiente
            onSend({
                type: type, // 'image', 'video', 'audio', 'document', 'voice'
                file: formData,
                fileInfo: fileInfo,
                // Si necesitas añadir un caption/mensaje al archivo
                content: message.trim() !== '' ? message : undefined
            });

            // Limpiar el campo de mensaje si se usó como caption
            setMessage('');
        }
    };

    const open = Boolean(anchorEl);

    // if (!userLoggedIn || !contactActive || !executiveMode) {
    //     return (
    //         <Box sx={{ padding: 3, backgroundColor: '#202c33' }}>
    //             {""}
    //         </Box>
    //     );
    // }

    return (
        <Box sx={{
            padding: 0,
            backgroundColor: '#202c33',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', padding: 2, backgroundColor: '#202c33' }}>
                {/* Input para archivo genérico */}
                <input
                    type="file"
                    ref={attachmentInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileChange(e, 'document')}
                />

                {/* Input para imágenes */}
                <input
                    type="file"
                    accept="image/*"
                    ref={imageInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileChange(e, 'image')}
                />

                {/* Input para videos */}
                <input
                    type="file"
                    accept="video/*"
                    ref={videoInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileChange(e, 'video')}
                />

                {/* Input para audios */}
                <input
                    type="file"
                    accept="audio/*"
                    ref={audioInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileChange(e, 'audio')}
                />

                {/* Botón para abrir el menú de adjuntos */}
                {typeMessagePermitted.length > 1 && (
                    <IconButton
                        onClick={handleAttachmentClick}
                        sx={{
                            color: '#fff',
                            marginRight: 1,
                            cursor: 'pointer',
                        }}
                    >
                        <MdAttachFile />
                    </IconButton>
                )}

                {/* Popover con opciones de adjuntos */}
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleAttachmentClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    PaperProps={{
                        style: {
                            backgroundColor: '#2a3942',
                            color: '#fff',
                            borderRadius: '12px',
                        }
                    }}
                >
                    <List sx={{ padding: 0 }}>
                        {
                            typeMessagePermitted.includes('image') ? (
                                <ListItem onClick={() => handleAttachmentSelect('image')} sx={{ cursor: 'pointer' }}>
                                    <ListItemIcon>
                                        <MdImage style={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Imagen" />
                                </ListItem>
                            ) : ""
                        }
                        {
                            typeMessagePermitted.includes('video') ? (
                                <ListItem onClick={() => handleAttachmentSelect('video')} sx={{ cursor: 'pointer' }}>
                                    <ListItemIcon>
                                        <MdVideocam style={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Video" />
                                </ListItem>
                            ) : ""
                        }
                        {
                            typeMessagePermitted.includes('audio') ? (
                                <ListItem onClick={() => handleAttachmentSelect('audio')} sx={{ cursor: 'pointer' }}>
                                    <ListItemIcon>
                                        <MdAudiotrack style={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Audio" />
                                </ListItem>
                            ) : ""
                        }
                        {
                            typeMessagePermitted.includes('document') ? (
                                <ListItem onClick={() => handleAttachmentSelect('document')} sx={{ cursor: 'pointer' }}>
                                    <ListItemIcon>
                                        <MdAttachFile style={{ color: '#fff' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Documento" />
                                </ListItem>
                            ) : ""
                        }

                    </List>
                </Popover>

                {/* Campo de texto para mensaje */}
                {
                    typeMessagePermitted.includes('text') ? (
                        <TextField
                            variant="outlined"
                            fullWidth
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Escribe un mensaje..."
                            sx={{
                                color: '#fff',
                                borderRadius: '10px',
                                backgroundColor: '#2a3942',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '10px',
                                    color: '#fff',
                                },
                            }}
                        />
                    ) : ""
                }

                {/* Botón para enviar mensaje o grabar audio */}
                {message.trim() ? (
                    <IconButton
                        onClick={handleSend}
                        sx={{
                            marginLeft: 1,
                            background: "none",
                            padding: 0,
                            cursor: 'pointer',
                        }}
                    >
                        <MdSend style={{ color: "#fff" }} />
                    </IconButton>
                ) : (
                    typeMessagePermitted.includes('voice') ? (
                        <IconButton
                            onClick={handleVoiceRecording}
                            sx={{
                                marginLeft: 1,
                                background: isRecording ? "rgba(255, 0, 0, 0.2)" : "none",
                                padding: 0,
                                cursor: 'pointer',
                            }}
                        >
                            <MdKeyboardVoice style={{ color: isRecording ? "#f00" : "#fff" }} />
                        </IconButton>
                    ) : ""
                )}
            </Box>
        </Box>
    );
};

export default ChatInput;